import { isInternalFeatureAccess } from '@electron/shared/utils';
import { AnalyticsEvents, isInternalEmail } from '@littlebird/ui-utils';
import { memoize } from 'lodash';
import { reaction } from 'mobx';
import { posthog } from 'posthog-js';

import { env, isDev, isProd, isRunningInLocalhost, posthogApiKey } from '@/env';
import {
  invalidateFeatureFlags,
  onFeatureFlagsLoaded,
  setFlagSource,
} from '@/features/feature-flags';
import { type User } from '@/query/user';
import { userStore } from '@/stores/user.store';
import { getBrowser, inBrowser, inElectron } from '@/utils/capabilities';
import { setSingularUser, trackDesktopAppInstalled } from '@/utils/singular';

const shouldTrackEvents = isProd && !isRunningInLocalhost;
const enableAnalyticsDebugLogs = false;

let resolvedEnvironmentProperties: Record<string, unknown> = {};

interface InitializePosthogOptions {
  disableSessionRecording?: boolean;
}

const captureEvent = memoize(
  (
    key: string,
    eventName: string,
    payload: Record<string, unknown>,
    sendInstantly: boolean
  ) => {
    posthog.capture(eventName, payload, { send_instantly: sendInstantly });
    setTimeout(() => captureEvent.cache.delete?.(key), 1000);
  },
  (key) => key
);

/**
 * Tracks an event using PostHog.
 * This function is type safe, enforcing that only pre-declared events and their payload types can be used.
 *
 * @param eventName - The name of the event, pre-declared in AnalyticsEvents.
 * @param payload - The payload for the event matching the declared type.
 */
export const trackEvent = (
  eventName: keyof typeof AnalyticsEvents,
  payload?: Record<string, unknown>,
  sendInstantly?: boolean
): void => {
  if (!shouldTrackEvents) return;
  const key = `${eventName}:${JSON.stringify(payload || {})}`;
  captureEvent(
    key,
    AnalyticsEvents[eventName],
    payload || {},
    sendInstantly ?? false
  );
};

// posthog-js reloads feature flags from setPersonProperties. Before init the
// SDK drops that request but keeps its in-flight marker, so the window never
// loads flags again until it is reloaded. Callers can run before init (the
// main window's consent analytics races the IPC awaits in initializePosthog),
// so hold their properties and apply them once the SDK is loaded.
let pendingUserProperties: Record<string, unknown> | undefined;

/**
 * Sets user properties in PostHog
 * @param properties - Object containing the properties to set
 */
export const setUserProperties = (
  properties: Record<string, unknown>
): void => {
  if (!posthog.__loaded) {
    pendingUserProperties = { ...pendingUserProperties, ...properties };
    return;
  }
  posthog.setPersonProperties(properties);
};

function flushPendingUserProperties(): void {
  if (!pendingUserProperties) return;
  const properties = pendingUserProperties;
  pendingUserProperties = undefined;
  posthog.setPersonProperties(properties);
}

/**
 * Submit a survey using PostHog.
 *
 * @param surveyName - The name of the survey
 * @param feedbacks - A list of responses for the survey questions
 */
export const submitSurvey = async (surveyName: string, feedbacks: string[]) => {
  return new Promise<void>((resolve, reject) => {
    posthog.getSurveys((surveys) => {
      const someSurvey = surveys.find(
        (survey) => survey.name.toLowerCase() === surveyName.toLowerCase()
      );

      if (!someSurvey) {
        reject(new Error(`survey with ${surveyName} not found`));
        return;
      }

      const responses = feedbacks.reduce(
        (table, feedback, index) => {
          table[`$survey_response_${someSurvey.questions[index].id}`] =
            feedback;

          return table;
        },
        {} as Record<string, string>
      );

      posthog.capture('survey sent', {
        $survey_id: someSurvey.id,
        $survey_questions: someSurvey.questions,
        ...responses,
      });

      resolve();
    });
  });
};

export function collectUserConfig() {
  const config = {
    environment: env,
    ...resolvedEnvironmentProperties,
  } as Record<string, unknown>;
  if (inElectron()) {
    config.platform = window.systemInfo.getPlatformName();
    config.window = window.systemInfo.currentWindow.label;
  }
  posthog.register_for_session(config);
}

export function identifyPersonInAnalytics(user: User): void {
  // Update analytics identity
  const initialDistinctId = posthog.get_distinct_id();
  if (initialDistinctId !== user.email)
    posthog.alias(initialDistinctId, user.email);

  // Determine user type
  const isInternalUser = isInternalEmail(user.email);

  // Build user properties object
  const userProperties = {
    email: user.email,
    user_id: user.id,
    ...(isInternalUser && { user_type: 'internal' }),
    ...resolvedEnvironmentProperties,
  };
  posthog.identify(user.email, userProperties);

  setSingularUser(user.id);
  trackDesktopAppInstalled();
}

/**
 * Collects environment-specific properties based on runtime context
 */
async function getEnvironmentProperties(): Promise<Record<string, unknown>> {
  const properties: Record<string, unknown> = {};

  if (inElectron()) {
    properties.desktop_app_version = await window.systemInfo.appVersion();
    properties.desktop_os = await window.systemInfo.osType();
    properties.desktop_os_version = window.systemInfo.systemVersion;
    properties.desktop_os_arch = window.systemInfo.arch;
    properties.desktop_app_type = 'electron';
  }

  if (inBrowser()) {
    properties.browser = await getBrowser();
    properties.browser_user_agent = navigator.userAgent;
  }

  return properties;
}

export function updateSessionRecordingSettings() {
  try {
    if (!shouldTrackEvents) return;

    const currentUser = userStore.user;
    const dataSharingEnabled = currentUser?.feature_flags?.data_sharing_enabled;

    const isInternal = isInternalFeatureAccess(currentUser?.feature_access);

    posthog.stopSessionRecording();

    if (dataSharingEnabled === true) {
      posthog.config.session_recording = {
        ...posthog.config.session_recording,
        maskAllInputs: false,
        maskInputOptions: { password: true },
        maskTextSelector: '',
      };
    } else {
      posthog.config.session_recording = {
        ...posthog.config.session_recording,
        maskAllInputs: true,
        maskInputOptions: { password: true },
        maskTextSelector: isInternal ? '*' : '.no-record',
      };
    }

    posthog.startSessionRecording();
  } catch (e) {
    console.error('Error updating session recording settings:', e);
  }
}

function initializeTracking(options: InitializePosthogOptions = {}) {
  const { disableSessionRecording = false } = options;
  try {
    const currentUser = userStore.user;
    if (currentUser?.email) {
      identifyPersonInAnalytics(currentUser);
    }

    let previousDataSharingEnabled =
      currentUser?.feature_flags?.data_sharing_enabled;

    reaction(
      () => userStore.user,
      (user, previousUser) => {
        if (user?.email !== previousUser?.email) invalidateFeatureFlags();
        if (user?.email) {
          identifyPersonInAnalytics(user);
        } else {
          posthog.reset();
        }

        const currentDataSharingEnabled =
          user?.feature_flags?.data_sharing_enabled;
        if (
          !disableSessionRecording &&
          currentDataSharingEnabled !== previousDataSharingEnabled
        ) {
          updateSessionRecordingSettings();
          previousDataSharingEnabled = currentDataSharingEnabled;
        }
      }
    );
  } catch (e) {
    console.error('Error initializing posthog:', e);
  }
}

/**
 * Initializes PostHog analytics tracking.
 * This function sets up the PostHog instance with the provided API key and registers user properties.
 * It also handles feature flag loading and user configuration collection.
 */
export const initializePosthog = async (
  options: InitializePosthogOptions = {}
) => {
  const { disableSessionRecording = false } = options;
  setFlagSource({
    getFeatureFlag: (key) => posthog.getFeatureFlag(key, { send_event: false }),
    getFeatureFlagPayload: (key) => posthog.getFeatureFlagPayload(key),
  });
  if (!posthogApiKey) {
    console.warn('VITE_POSTHOG_API_KEY is not set, not loading feature flags.');
    return;
  }

  resolvedEnvironmentProperties = await getEnvironmentProperties();

  const initOptions: Parameters<typeof posthog.init>[1] = {
    api_host: 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    autocapture: !isDev && !isRunningInLocalhost,
    capture_pageview: true,
    debug: enableAnalyticsDebugLogs,
    loaded: () => {
      initializeTracking(options);
      flushPendingUserProperties();
      collectUserConfig();
      if (disableSessionRecording) {
        posthog.stopSessionRecording();
      } else {
        updateSessionRecordingSettings();
      }
    },
  };
  if (disableSessionRecording) {
    (initOptions as Record<string, unknown>).disable_session_recording = true;
  }

  posthog.init(posthogApiKey, initOptions);

  // Dev-only: expose the instance so the documented feature-flag override
  // workflow (`posthog.featureFlags.override(...)`) works from the console.
  if (isDev) {
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
  }

  posthog.onFeatureFlags((_flags, _variants, context) =>
    onFeatureFlagsLoaded(context)
  );

  if (import.meta.env.DEV) {
    // The flag-override workflow documented in apps/desktop/AGENTS.md
    // (posthog.featureFlags.override({...}) in the devtools console) needs
    // the instance on window; the bundled module does not expose it by
    // itself. Gated on the Vite dev server, not VITE_ENV: a dev-server run
    // with VITE_ENV=production still needs the console workflow, while
    // packaged builds never get it.
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
  }
};
